import { useCurrentCommunityMember } from "@circle-react/contexts/punditUserContext";
import {
  postInvalidations,
  postQueries,
} from "@circle-react/lib/queries/posts";
import { postQueryKeys } from "@circle-react/lib/queries/posts/constants";
import { matchPostDetailsBatchQuery } from "@circle-react/lib/queries/posts/helpers";
import type { PostDetailRecord } from "@circle-react/lib/queries/posts/types";
import type { ReactNode } from "react";
import { createContext, useContext } from "react";
import { useQueries, useQueryClient } from "react-query";

interface PostsDetailsContextProviderProps {
  children?: ReactNode;
  isSharedPostView?: boolean;
  perPagePostIds?: number[][];
  queryOptions?: any;
  spaceId?: number;
}

const PostsDetailsContext = createContext({});
PostsDetailsContext.displayName = "PostsDetailsContext";

export const PostsDetailsContextProvider = ({
  spaceId,
  perPagePostIds = [],
  isSharedPostView = false,
  queryOptions = {},
  children,
}: PostsDetailsContextProviderProps) => {
  const queryClient = useQueryClient();
  const currentCommunityMember = useCurrentCommunityMember();

  const postDetailsQuery = useQueries({
    queries: perPagePostIds.map(postIds => ({
      ...postQueries.postBatchDetails({ postIds, spaceId, isSharedPostView }),
      ...queryOptions,
    })),
  });

  const allPostDetails: Record<
    number,
    { data: PostDetailRecord | null; isLoading: boolean }
  > = Object.fromEntries(
    perPagePostIds
      .flat()
      .map(postId => [postId, { data: null, isLoading: true }]),
  );

  postDetailsQuery.forEach(result => {
    const data = Array.isArray(result.data) ? result.data : undefined;

    data?.forEach(postDetail => {
      allPostDetails[postDetail.id] = {
        data: postDetail,
        isLoading: result.isPending,
      };
    });
  });

  const refetchPostDetails = (postId: number) => {
    void postInvalidations.postDetails(postId);
  };

  const updatePostDetailsQueryCache = (postId: number, updates: any) => {
    queryClient.setQueriesData<PostDetailRecord[]>(
      {
        queryKey: postQueryKeys.detail,
        predicate: matchPostDetailsBatchQuery(postId),
      },
      (prev = []) =>
        prev.map(postDetail => {
          if (postDetail.id !== postId) return postDetail;
          return { ...postDetail, ...updates };
        }),
    );
  };

  const onLikeToggle = (postDetailsData: any, value: boolean) => {
    if (!currentCommunityMember || !postDetailsData) {
      return;
    }

    let postUpdates = {};
    if (value) {
      postUpdates = {
        is_liked_by_current_user: value,
        sample_user_likes_community_members: [
          ...postDetailsData.sample_user_likes_community_members,
          currentCommunityMember,
        ],
        user_likes_count: postDetailsData.user_likes_count + 1,
      };
    } else {
      postUpdates = {
        is_liked_by_current_user: value,
        sample_user_likes_community_members:
          postDetailsData.sample_user_likes_community_members.filter(
            (cm: any) => cm.id !== currentCommunityMember.id,
          ),
        user_likes_count: postDetailsData.user_likes_count - 1,
      };
    }

    updatePostDetailsQueryCache(postDetailsData.id, postUpdates);

    void postInvalidations.postLikes(postDetailsData.id);

    // DEPRECATED: Remove after 4.0 launch
    queryClient.removeQueries({
      queryKey: ["post-likes-modal", postDetailsData.id],
    });
  };

  const value = {
    allPostDetails,
    onLikeToggle,
    refetchPostDetails,
    updatePostDetailsQueryCache,
  };
  return (
    <PostsDetailsContext.Provider value={value}>
      {children}
    </PostsDetailsContext.Provider>
  );
};

export const usePostDetails = (postId: number) => {
  const postDetailsContext: any = useContext(PostsDetailsContext);

  if (!postId || !postDetailsContext.allPostDetails?.[postId]) {
    return {};
  }

  const { data, isLoading } = postDetailsContext.allPostDetails[postId];

  return {
    isLoading,
    postDetails: data,
    onLikeToggle: (value: any) => postDetailsContext.onLikeToggle(data, value),
    refetchPostDetails: () => postDetailsContext.refetchPostDetails(postId),
    updatePostDetailsQueryCache: (updates: any) =>
      postDetailsContext.updatePostDetailsQueryCache(postId, updates),
  };
};
